import { useEffect, useMemo, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { checkoutCountryList as countryList } from 'shared/data/siteSettings';
import { Account } from 'shared/types/account';
import { Address } from 'shared/types/account/Address';
import Checkbox from 'components/commercetools-ui/atoms/checkbox';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Input from 'components/commercetools-ui/atoms/input';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import useValidate from 'helpers/hooks/useValidate';
import countryStates from 'static/states.json';
import { useAccount } from 'frontastic';
import AfdComponent from 'frontastic/tastics/address-afd';
import DeleteModal from './deleteModal';
import usePropsToAddressType from './mapPropsToAddressType';
import AccountForm from '../../account-atoms/account-form';
import SaveOrCancel from '../../account-atoms/save-or-cancel';
import useDiscardForm from '../../hooks/useDiscardForm';
import useFeedbackToasts from '../../hooks/useFeedbackToasts';

export interface AddressFormProps {
  addressId?: string;
  editedAddressId?: Address['addressId'];
}

export interface AddressFormData extends Address {
  addressId: string;
  addressType?: 'shipping' | 'billing';
  isDefaultAddress?: boolean;
  isBillingAddress?: boolean;
  isDefaultBillingAddress?: boolean;
  isDefaultShippingAddress?: boolean;
  email: string;
}

interface AddressInfo {
  addressType?: string;
  line1?: string;
  line2?: string;
  postcode?: string;
  town?: string;
  postalCounty?: string;
}

type AddressType = 'shipping' | 'billing';
type AddressTypeOptions = Array<{ label: string; value: AddressType }>;

const AddressForm: React.FC<AddressFormProps> = ({ editedAddressId }) => {
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });

  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });

  const { formatMessage } = useFormat({ name: 'common' });

  const { validateTextExists, validatePostalCode, validatePhoneNumeric, validateEmail } = useValidate();

  const { removeAddress, account } = useAccount();

  const { mapPropsToAddress } = usePropsToAddressType();

  const { discardForm } = useDiscardForm();

  const { notifyWentWrong } = useFeedbackToasts();

  const { getCountry } = useLocale();

  const states = countryStates[getCountry() as keyof typeof countryStates] ?? [];

  const [loading, setLoading] = useState(false);

  const [loadingDelete, setLoadingDelete] = useState(false);

  const toggleLoadingOn = () => setLoading(true);

  const toggleLoadingOff = () => setLoading(false);

  const [selectedTitle, setSelectedTitle] = useState('');

  // New address data
  const defaultData = useMemo(() => {
    const country = getCountry();
    if (!editedAddressId) return { country, title: 'Mr' } as AddressFormData;

    const accountAddress = account?.addresses?.find(
      (address) => address.addressId === editedAddressId,
    ) as AddressFormData;

    if (accountAddress) {
      accountAddress.addressType = mapPropsToAddress(accountAddress).addressType;
    }

    return accountAddress;
  }, [account?.addresses, getCountry, editedAddressId, mapPropsToAddress]);

  const [data, setData] = useState<AddressFormData>(defaultData);

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const addressTypes: AddressTypeOptions = [
    { label: formatCheckoutMessage({ id: 'shippingAddress', defaultMessage: 'Shipping Address' }), value: 'shipping' },
    { label: formatCheckoutMessage({ id: 'billingAddress', defaultMessage: 'Billing Address' }), value: 'billing' },
  ];

  const formTitle = formatAccountMessage(
    editedAddressId
      ? { id: 'address.edit', defaultMessage: 'Edit address' }
      : { id: 'address.add', defaultMessage: 'Add an address' },
  );

  useEffect(() => {
    setData(defaultData);
  }, []);

  const updateData = (name: string, value: boolean | string) => {
    setData({ ...data, [name]: value });
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    updateData(e.target.name, e.target.value);
  };

  // const discardFormAndNotify = (promise: Promise<Account | void>) => {
  //   promise.then(toggleLoadingOff).then(discardForm).then(notifyDataUpdated).catch(notifyWentWrong);
  // };

  const handleUpdateAddress = (promise: Promise<Account | void>) => {
    promise
      .then(toggleLoadingOff)
      .then(discardForm)
      .then(() => {
        localStorage.setItem('addressAction', 'update');
      })
      .catch(notifyWentWrong);
  };

  const handleAddAddress = (promise: Promise<Account | void>) => {
    promise
      .then(toggleLoadingOff)
      .then(() => {
        localStorage.setItem('addressAction', 'add');
      })
      .then(discardForm)
      .catch(notifyWentWrong);
  };

  const handleDelete = () => {
    setLoadingDelete(true);

    removeAddress(data.addressId)
      .then(() => setLoadingDelete(false))
      .then(closeModal)
      .then(() => {
        localStorage.setItem('addressAction', 'delete');
      })
      .then(discardForm);
  };

  const onAFDAddressSelection = (addressInfo: AddressInfo) => {
    setData({
      ...data,
      streetName: addressInfo.line1 || '',
      additionalAddressInfo: addressInfo.line2 || '',
      city: addressInfo.town || '',
      county: addressInfo.postalCounty || '',
      postalCode: addressInfo.postcode || '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    const validPostalCode = validatePostalCode(
      data['postalCode' as keyof Address] as string,
      data['country' as keyof Address] as string,
    );
    const validPhoneNumber = validatePhoneNumeric(data['phone' as keyof Address] as string);
    //const validEmail = validateEmail(data['email' as keyof Address] as string);
    if (!validPostalCode || !validPhoneNumber) {
      return false;
    }
    data['county'] = data['county'] || ' ';
    e.preventDefault();
    toggleLoadingOn();

    const { addAddress, updateAddress } = mapPropsToAddress(data);

    if (editedAddressId) {
      if (defaultData.addressType !== data.addressType) {
        handleUpdateAddress(removeAddress(defaultData.addressId).then(addAddress));
      } else {
        handleUpdateAddress(updateAddress());
      }

      return;
    }
    handleAddAddress(addAddress());
  };

  const dropdownList =
    countryList.length > 0
      ? [...countryList.map(({ countryCode, country }) => ({ label: country, value: countryCode }))]
      : [];

  const handleTitleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTitle(e.target.value);
    updateData(e.target.name, e.target.value);
  };

  const salutations = [
    { label: 'Mr', value: 'Mr' },
    { label: 'Mrs', value: 'Mrs' },
    { label: 'Ms', value: 'Ms' },
    { label: 'Miss', value: 'Miss' },
    { label: 'Dr', value: 'Dr' },
  ];

  return (
    <AccountForm
      onSubmit={handleSubmit}
      title={formTitle}
      loading={loading}
      containerClassName="grid grid-cols-4 gap-20 md:px-24 md:px-0"
    >
      <div className="col-span-4">
        <Dropdown
          name="title"
          value={selectedTitle}
          items={salutations}
          onChange={handleTitleChange}
          className="w-full border-neutral-500 text-16 font-light"
          label="Title"
          isRequired={true}
        />
      </div>

      <div className="col-span-4 lg:col-span-2">
        <Input
          label={formatMessage({ id: 'firstName', defaultMessage: 'First Name' })}
          required
          type="text"
          name="firstName"
          id="first-name"
          value={data?.firstName ?? ''}
          autoComplete="first-name"
          onChange={handleChange}
          validation={validateTextExists}
          maxLength={21}
        />
      </div>

      <div className="col-span-4 lg:col-span-2">
        <Input
          label={formatMessage({ id: 'lastName', defaultMessage: 'Last Name' })}
          required
          type="text"
          name="lastName"
          id="last-name"
          value={data?.lastName ?? ''}
          autoComplete="last-name"
          onChange={handleChange}
          validation={validateTextExists}
          maxLength={31}
        />
      </div>

      <div className="col-span-4 lg:col-span-2">
        <Input
          label={formatMessage({ id: 'phone', defaultMessage: 'Mobile' })}
          required
          type="text"
          name="phone"
          id="phone"
          value={data?.phone ?? ''}
          autoComplete="phone"
          onChange={handleChange}
          validation={validatePhoneNumeric}
          isValid={!!data['phone' as keyof Address] && validatePhoneNumeric(data['phone' as keyof Address] as string)}
          maxLength={21}
        />
      </div>

      <div className="col-span-4">
        <Dropdown
          name="country"
          value={data.country}
          items={dropdownList}
          onChange={handleChange}
          className="w-full text-16 font-light"
          label="Country"
          isRequired={true}
        />
      </div>

      {data.country == 'GB' && <AfdComponent callToParent={onAFDAddressSelection} />}

      <div className="col-span-4 lg:col-span-2">
        <Input
          label={`${formatMessage({ id: 'address', defaultMessage: 'Address' })} 1`}
          type="text"
          required
          name="streetName"
          id="street-name"
          value={data?.streetName ?? ''}
          autoComplete="primary-address"
          onChange={handleChange}
        />
      </div>

      <div className="col-span-4 lg:col-span-2">
        <Input
          label={`${formatMessage({ id: 'address', defaultMessage: 'Address' })} 2 (${formatMessage({
            id: 'optional',
            defaultMessage: 'Optional',
          })})`}
          type="text"
          name="additionalAddressInfo"
          id="additional-address-info"
          value={data?.additionalAddressInfo ?? ''}
          autoComplete="additional-address-info"
          onChange={handleChange}
        />
      </div>

      <div className="col-span-4 lg:col-span-2">
        <Input
          label={formatMessage({ id: 'city', defaultMessage: 'City' })}
          required
          type="text"
          name="city"
          id="city"
          value={data?.city ?? ''}
          autoComplete="city"
          onChange={handleChange}
          validation={validateTextExists}
        />
      </div>

      <div className="col-span-4 lg:col-span-2">
        <Input
          label={formatMessage({ id: 'county', defaultMessage: 'County' })}
          type="text"
          name="county"
          id="county"
          value={data?.county ?? ''}
          autoComplete="county"
          onChange={handleChange}
        />
      </div>

      <div className="col-span-4 lg:col-span-2">
        <Input
          label={formatMessage({ id: 'zipCode', defaultMessage: 'Postal Code' })}
          required
          type="text"
          name="postalCode"
          id="postal-code"
          value={data?.postalCode ?? ''}
          autoComplete="postal-code"
          isValid={
            !!data['postalCode' as keyof Address] &&
            validatePostalCode(
              data['postalCode' as keyof Address] as string,
              data['country' as keyof Address] as string,
            )
          }
          onChange={handleChange}
        />
      </div>

      {states.length > 0 && (
        <div className="col-span-4 lg:col-span-2">
          <Dropdown
            name="state"
            value={data.state ?? ''}
            items={[{ label: '', value: '' }, ...states.map(({ name, code }) => ({ label: name, value: code }))]}
            className="w-full"
            onChange={handleChange}
            label={formatMessage({
              id: 'state',
              defaultMessage: 'State',
            })}
          />
        </div>
      )}

      <div className="col-span-4">
        <Dropdown
          name="addressType"
          items={addressTypes}
          className="w-full"
          onChange={handleChange}
          value={data.addressType ?? ''}
          defaultValue={editedAddressId ? mapPropsToAddress(data).addressType : addressTypes[0].value}
          label={formatAccountMessage({
            id: 'address.type',
            defaultMessage: 'Address type',
          })}
        />
      </div>

      <div className="col-span-4">
        <Checkbox
          name="isDefaultAddress"
          id="is-default-address"
          checked={data?.isDefaultBillingAddress || data?.isDefaultShippingAddress || false}
          onChange={({ name, checked }) => updateData(name, checked)}
          containerClassName="mt-4 md:mb-20 mb-12"
          label={formatAccountMessage({
            id: 'address.setDefault',
            defaultMessage: 'Save as default address',
          })}
        />
      </div>

      <div className="col-span-4">
        <div className="grid h-fit items-center justify-between gap-32 md:mt-20 md:flex md:gap-0">
          {editedAddressId && (
            <div
              className="flex items-center gap-8 hover:cursor-pointer hover:opacity-[0.7]"
              onClick={() => setModalIsOpen(true)}
            >
              <TrashIcon className="h-20 w-20 text-secondary-black" />
              <Typography className="text-14 leading-[114%] text-secondary-black" as="span">
                {formatMessage({ id: 'delete', defaultMessage: 'Delete' })}
              </Typography>
            </div>
          )}

          <SaveOrCancel onCancel={discardForm} loading={loading} />
        </div>

        <DeleteModal
          modalIsOpen={modalIsOpen}
          loading={loadingDelete}
          closeModal={closeModal}
          handleDelete={handleDelete}
        />
      </div>
    </AccountForm>
  );
};

export default AddressForm;
